import React, { useState, useEffect, useContext } from 'react';
import { useSettings } from '../../contexts/SettingsContext';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Menu,
  MenuList,
  MenuItem as MenuListItem,
  ListItemIcon,
  ListItemText,
  Fab,
  Tooltip,
  Badge,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemAvatar,
  Divider,
  CircularProgress,
  Grow,
  LinearProgress,
  Skeleton,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Star as StarIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as XCircleIcon,
  Visibility as VisibilityIcon,
  GetApp as ExportIcon,
  FilterList as FilterIcon,
  History as HistoryIcon,
  Payment as PaymentIcon,
  Flight as FlightIcon,
  Hotel as HotelIcon,
  Assessment as AssessmentIcon,
  BarChart,
  ViewList as TableViewIcon,
  ViewModule as CardViewIcon
} from '@mui/icons-material';
import { SettingsContext } from '../../contexts/SettingsContext';
import { 
  useClientsData, 
  useReservationsData, 
  useFinancialData,
  addClient,
  updateClient,
  deleteClient
} from '../../services/dataService';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const Clients = () => {
  const { settings, formatCurrency, formatDate } = useSettings();
  const navigate = useNavigate();
  
  // Get data from data services with default empty arrays
  const { clients: clientsData = [], loading: clientsLoading, error: clientsError } = useClientsData();
  const { reservations: reservationsData = [], loading: reservationsLoading, error: reservationsError } = useReservationsData();
  const { transactions: financialData = [], loading: financialLoading, error: financialError } = useFinancialData();
  
  // Local state
  const [filteredClients, setFilteredClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [detailsTab, setDetailsTab] = useState(0);
  const [activeTab, setActiveTab] = useState('all'); // Nouvel état pour les onglets de filtration rapide
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'card'
  // Toggle for displaying Actions Rapides sections
  const showQuickActions = false;
  
  // Chargement optimisé - affichage immédiat de la page clients
  const isLoading = false;
  
  // Form data for adding/editing clients
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    adresse: '',
    ville: '',
    pays: '',
    statut: 'Actif'
  });
  
  // Helper function to ensure consistent ID comparison
  const normalizeId = (id) => {
    return id !== undefined && id !== null ? Number(id) : null;
  };
  
  // Process client data with reservations and financial data
  const processedClients = React.useMemo(() => {
    if (clientsLoading || reservationsLoading || financialLoading) {
      return [];
    }
    
    // Ensure all data arrays are safe before processing
    const safeClientsData = Array.isArray(clientsData) ? clientsData : [];
    const safeReservationsData = Array.isArray(reservationsData) ? reservationsData : [];
    const safeFinancialData = Array.isArray(financialData) ? financialData : [];
    
    return safeClientsData.map(client => {
      // Normalize client ID for consistent comparison
      const normalizedClientId = normalizeId(client.id);
      
      // Count client's reservations - match both numeric and string IDs
      const clientReservations = safeReservationsData.filter(r => 
        normalizeId(r.clientId) === normalizedClientId
      );
      const nombreReservations = clientReservations.length;
      
      // Calculate total spent - match both numeric and string IDs
      const clientTransactions = safeFinancialData.filter(t => 
        normalizeId(t.clientId) === normalizedClientId
      );
      const totalDepense = clientTransactions.reduce((sum, t) => sum + (t.montant || 0), 0);
      
      // Determine last visit date (most recent reservation)
      const sortedReservations = [...clientReservations].sort(
        (a, b) => new Date(b.dateReservation) - new Date(a.dateReservation)
      );
      const derniereVisite = sortedReservations.length > 0 
        ? sortedReservations[0].dateReservation 
        : client.dateInscription || '-';
      
      // Add status if not present
      const statut = client.statut || (nombreReservations > 0 ? 'Actif' : 'Inactif');
      
      // Add inscription date if not present
      const dateInscription = client.dateInscription || '2024-01-01'; // Default date
      
      // Handle location data - preserve existing values if present
      let ville = client.ville;
      let pays = client.pays;
      
      // Only try to extract or provide location data if both ville and pays are missing
      if ((!ville || !pays) && client.adresse) {
        const addressParts = client.adresse.split(',');
        if (addressParts.length >= 2) {
          ville = ville || addressParts[0].trim();
          pays = pays || addressParts[addressParts.length - 1].trim();
        } else if (addressParts.length === 1) {
          // If only one part, use it as ville
          ville = ville || addressParts[0].trim();
        }
      }
      
      // Only provide sample data if values are still missing
      if ((!ville || !pays) && client.id) {
        // Map well-known client IDs to locations from sample data
        const locationMap = {
          1: { ville: 'Alger', pays: 'Algérie' },
          2: { ville: 'Oran', pays: 'Algérie' },
          3: { ville: 'Constantine', pays: 'Algérie' },
          4: { ville: 'Annaba', pays: 'Algérie' },
          5: { ville: 'Tlemcen', pays: 'Algérie' }
        };
        
        if (locationMap[client.id]) {
          ville = ville || locationMap[client.id].ville;
          pays = pays || locationMap[client.id].pays;
        }
      }
      
      return {
        ...client,
        nombreReservations,
        totalDepense,
        derniereVisite,
        statut,
        dateInscription,
        niveauFidelite: client.niveauFidelite || 'Bronze',
        preferencesVoyage: client.preferencesVoyage || [],
        ville: ville || '',
        pays: pays || ''
      };
    });
  }, [clientsData, reservationsData, financialData, clientsLoading, reservationsLoading, financialLoading]);

  // Debounced search term to prevent excessive filtering
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  
  // Debounce search term to prevent excessive updates
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300); // 300ms delay
    
    return () => clearTimeout(timer);
  }, [searchTerm]);
  
  // Update filtered clients when data changes
  useEffect(() => {
    if (processedClients.length > 0) {
      let filtered = processedClients.filter(client => {
        const matchesSearch = 
          client.nom?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
          client.prenom?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
          client.email?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
          (client.telephone && client.telephone.includes(debouncedSearchTerm));
        
        // Filtre basé sur l'onglet actif
        let matchesActiveTab = true;
        if (activeTab === 'actif') matchesActiveTab = client.statut === 'Actif';
        else if (activeTab === 'inactif') matchesActiveTab = client.statut === 'Inactif';
        else if (activeTab === 'premium') matchesActiveTab = client.niveauFidelite === 'Premium';
        else if (activeTab === 'standard') matchesActiveTab = client.niveauFidelite === 'Standard';
        else if (activeTab === 'bronze') matchesActiveTab = client.niveauFidelite === 'Bronze';
        
        // Filtre de statut supplémentaire (dropdown)
        const matchesFilter = filterStatus === 'all' || client.statut === filterStatus;
        
        return matchesSearch && matchesFilter && matchesActiveTab;
      });
      
      setFilteredClients(filtered);
      setPage(0);
    }
  }, [debouncedSearchTerm, filterStatus, processedClients, activeTab]);

  // Pagination for card view
  const paginatedClients = filteredClients.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // Helper functions
  const getStatusColor = (statut) => {
    switch (statut) {
      case 'Actif': return 'rgba(34, 197, 94, 0.8)';
      default: return 'rgba(107, 114, 128, 0.8)';
    }
  };

  const getFidelityColor = (niveauFidelite) => {
    switch (niveauFidelite) {
      case 'Premium': return 'rgba(239, 68, 68, 0.8)';
      case 'Standard': return 'rgba(59, 130, 246, 0.8)';
      case 'Bronze': return 'rgba(245, 158, 11, 0.8)';
      default: return 'rgba(107, 114, 128, 0.8)';
    }
  };

  const getClientReservationsCount = (clientId) => {
    if (!reservationsData || reservationsData.length === 0) return 0;
    return reservationsData.filter(reservation => reservation.clientId === clientId).length;
  };

  const handleViewClient = (client) => {
    setSelectedClient(client);
    setOpenDetailsDialog(true);
  };

  const handleEditClient = (client) => {
    setEditingClient(client);
    setFormData({
      nom: client.nom,
      prenom: client.prenom,
      email: client.email,
      telephone: client.telephone,
      adresse: client.adresse || '',
      ville: client.ville || '',
      pays: client.pays || '',
      statut: client.statut,
      preferencesVoyage: client.preferencesVoyage || []
    });
    setOpenDialog(true);
  };

  const handleOpenDialog = (client = null) => {
    if (client) {
      setEditingClient(client);
      setFormData({
        nom: client.nom,
        prenom: client.prenom,
        email: client.email,
        telephone: client.telephone,
        adresse: client.adresse || '',
        ville: client.ville || '',
        pays: client.pays || '',
        statut: client.statut
      });
    } else {
      setEditingClient(null);
      setFormData({
        nom: '',
        prenom: '',
        email: '',
        telephone: '',
        adresse: '',
        ville: '',
        pays: '',
        statut: 'Actif'
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingClient(null);
    setFormData({
      nom: '',
      prenom: '',
      email: '',
      telephone: '',
      adresse: '',
      ville: '',
      pays: '',
      statut: 'Actif'
    });
  };

  const handleSaveClient = async () => {
    // Validate form data - only nom is required
    if (!formData.nom || !formData.nom.trim()) {
      setSnackbar({
        open: true,
        message: 'Veuillez remplir au moins le nom',
        severity: 'error'
      });
      return;
    }
    
    try {
      if (editingClient) {
        // Update an existing client with all necessary fields
        const updatedClient = { 
          ...editingClient, 
          nom: formData.nom,
          prenom: formData.prenom,
          // Fix email validation issue: send null if email is empty
          email: formData.email.trim() || null,
          telephone: formData.telephone,
          adresse: formData.adresse,
          ville: formData.ville,
          pays: formData.pays,
          statut: formData.statut
        };
        
        // Use the direct update function
        await updateClient(updatedClient);
        
        setSnackbar({
          open: true,
          message: 'Client modifié avec succès',
          severity: 'success'
        });
      } else {
        // Create a new client
        const newClient = {
          ...formData,
          // Fix email validation issue: send null if email is empty
          email: formData.email.trim() || null,
          id: Date.now().toString(), // Generate unique ID as string
          dateInscription: new Date().toISOString().split('T')[0],
          nombreReservations: 0,
          totalDepense: 0,
          derniereVisite: '-',
          niveauFidelite: 'Bronze',
          preferencesVoyage: []
        };
        
        // Use the direct add function
        await addClient(newClient);
        
        setSnackbar({
          open: true,
          message: 'Client ajouté avec succès',
          severity: 'success'
        });
      }
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving client:', error);
      setSnackbar({
        open: true,
        message: `Erreur: ${error.message}`,
        severity: 'error'
      });
    }
  };

  const handleDeleteClient = async (clientId) => {
    try {
      // Await the deletion request so we are sure the backend processed it
      await deleteClient(clientId);

      // Optimistically update local UI by filtering out the deleted client
      setFilteredClients(prev => prev.filter(c => c.id !== clientId));

      setSnackbar({
        open: true,
        message: 'Client supprimé avec succès',
        severity: 'success'
      });
    } catch (error) {
      console.error('Erreur lors de la suppression du client:', error);
      setSnackbar({
        open: true,
        message: `Erreur: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setAnchorEl(null);
    }
  };

  const handleMenuClick = (event, client) => {
    setAnchorEl(event.currentTarget);
    setSelectedClient(client);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedClient(null);
  };

  const handleViewDetails = () => {
    setOpenDetailsDialog(true);
    setAnchorEl(null);
  };

  const getInitials = (nom, prenom) => {
    const firstLetter = nom && typeof nom === 'string' ? nom.charAt(0) : '';
    const secondLetter = prenom && typeof prenom === 'string' ? prenom.charAt(0) : '';
    return `${firstLetter}${secondLetter}`.toUpperCase();
  };

  const getClientReservations = (clientId) => {
    if (reservationsLoading || !reservationsData) {
      return [];
    }
    const normalizedClientId = normalizeId(clientId);
    return reservationsData.filter(r => normalizeId(r.clientId) === normalizedClientId);
  };

  const getClientTransactions = (clientId) => {
    if (financialLoading || !financialData) {
      return [];
    }
    const normalizedClientId = normalizeId(clientId);
    return financialData.filter(t => normalizeId(t.clientId) === normalizedClientId);
  };

  const getFidelityColorForChip = (niveau) => {
    switch(niveau) {
      case 'Premium': return 'error';
      case 'Standard': return 'info';
      case 'Bronze': return 'warning';
      default: return 'default';
    }
  };

  // Calculate stats for the dashboard cards
  const statsCards = React.useMemo(() => {
    // Default cards with static values to prevent flickering during form input
    const defaultCards = [
      { title: 'Total Clients', value: processedClients.length || 0, icon: PeopleIcon, color: 'primary', trend: '+12%' },
      { title: 'Clients Actifs', value: processedClients.filter(c => c.statut === 'Actif').length || 0, icon: TrendingUpIcon, color: 'success', trend: '0%' },
      { title: 'Nouveaux ce mois', value: 0, icon: PersonIcon, color: 'info', trend: '+8%' },
      { title: 'Revenus Total', value: formatCurrency(0), icon: PaymentIcon, color: 'warning', trend: '+15%' }
    ];
    
    if (isLoading) {
      return [
        { title: 'Total Clients', value: '-', icon: PeopleIcon, color: 'primary', trend: '-' },
        { title: 'Clients Actifs', value: '-', icon: TrendingUpIcon, color: 'success', trend: '-' },
        { title: 'Nouveaux ce mois', value: '-', icon: PersonIcon, color: 'info', trend: '-' },
        { title: 'Revenus Total', value: '-', icon: PaymentIcon, color: 'warning', trend: '-' }
      ];
    }
    
    // If we're in the middle of form input (dialog is open), return the default cards
    if (openDialog) {
      return defaultCards;
    }

    try {
      const currentMonth = new Date().getMonth();
      const activeClients = processedClients.filter(c => c.statut === 'Actif').length;
      const newClients = processedClients.filter(c => {
        try {
          const inscriptionDate = new Date(c.dateInscription);
          return inscriptionDate.getMonth() === currentMonth;
        } catch (e) {
          return false;
        }
      }).length;
      const totalRevenue = processedClients.reduce((sum, c) => sum + (c.totalDepense || 0), 0);

      return [
        {
          title: 'Total Clients',
          value: processedClients.length,
          icon: PeopleIcon,
          color: 'primary',
          trend: '+12%'
        },
        {
          title: 'Clients Actifs',
          value: activeClients,
          icon: TrendingUpIcon,
          color: 'success',
          trend: processedClients.length > 0 ? `${Math.round((activeClients / processedClients.length) * 100)}%` : '0%'
        },
        {
          title: 'Nouveaux ce mois',
          value: newClients,
          icon: PersonIcon,
          color: 'info',
          trend: '+8%'
        },
        {
          title: 'Revenus Total',
          value: formatCurrency(totalRevenue),
          icon: PaymentIcon,
          color: 'warning',
          trend: '+15%'
        }
      ];
    } catch (error) {
      console.error('Error calculating stats cards:', error);
      return defaultCards;
    }
  }, [processedClients, isLoading, formatCurrency, openDialog]);

  // Function to format currency correctly
  const formatCurrencyForPDF = (amount) => {
    // Convert number to string with spaces as thousand separators
    const numberPart = Math.round(amount || 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    return `${numberPart} DZD`;
  };

  const exportClientsToPDF = () => {
    try {
      // Create a new PDF document
      const doc = new jsPDF('landscape');
      
      // Document dimensions
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 15;
      
      // Colors
      const primaryColor = [30, 64, 175]; // blue
      const secondaryColor = [124, 58, 237]; // purple
      const textColor = [31, 41, 55]; // dark gray
      
      // Add header
      doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.rect(0, 0, pageWidth, 25, 'F');
      
      // Replace logo placeholder with agency logo and name
      if (settings.agencyLogo) {
        try {
          // Add the agency logo from base64 string
          doc.addImage(settings.agencyLogo, 'PNG', margin, 5, 20, 15, undefined, 'FAST');
          
          // Add agency name
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(12);
          doc.setFont('helvetica', 'bold');
          doc.text(settings.agencyName || 'Agence de Voyage', margin + 25, 15);
        } catch (logoError) {
          console.error('Error adding logo to PDF:', logoError);
          // Fallback to text only
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(12);
          doc.setFont('helvetica', 'bold');
          doc.text(settings.agencyName || 'Agence de Voyage', margin, 15);
        }
      } else {
        // No logo, just add agency name
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text(settings.agencyName || 'Agence de Voyage', margin, 15);
      }
      
      // Add title
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text("Liste des Clients", pageWidth - margin, 17, { align: 'right' });
      
      // Add date
      doc.setTextColor(textColor[0], textColor[1], textColor[2]);
      doc.setFontSize(10);
      doc.text(`Généré le: ${new Date().toLocaleDateString('fr-FR')}`, margin, 35);
      
      // Add summary section
      doc.setDrawColor(secondaryColor[0], secondaryColor[1], secondaryColor[2]);
      doc.setLineWidth(0.5);
      doc.line(margin, 40, pageWidth - margin, 40);
      
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text("Résumé", margin, 50);
      
      const stats = [
        { label: "Nombre total de clients", value: `${filteredClients.length}` },
        { label: "Clients actifs", value: `${filteredClients.filter(c => c.statut === 'Actif').length} (${Math.round(filteredClients.filter(c => c.statut === 'Actif').length / filteredClients.length * 100)}%)` },
        { label: "Clients premium", value: `${filteredClients.filter(c => c.niveauFidelite === 'Premium').length} (${Math.round(filteredClients.filter(c => c.niveauFidelite === 'Premium').length / filteredClients.length * 100)}%)` },
        { label: "Revenu total", value: formatCurrencyForPDF(filteredClients.reduce((sum, client) => sum + (client.totalDepense || 0), 0)) },
        { label: "Panier moyen", value: formatCurrencyForPDF(filteredClients.reduce((sum, client) => sum + (client.totalDepense || 0), 0) / filteredClients.filter(c => c.totalDepense > 0).length) }
      ];
      
      doc.autoTable({
        startY: 55,
        head: [['Indicateur', 'Valeur']],
        body: stats.map(item => [item.label, item.value]),
        theme: 'grid',
        headStyles: { fillColor: primaryColor, textColor: [255, 255, 255], fontStyle: 'bold' },
        styles: { fontSize: 10 },
        margin: { left: margin, right: margin },
        tableWidth: 'auto',
        alternateRowStyles: { fillColor: [245, 247, 250] }
      });
      
      // Add clients table
      const tableColumn = [
        "Nom", 
        "Email", 
        "Téléphone", 
        "Localisation", 
        "Statut", 
        "Fidélité", 
        "Réservations", 
        "Total Dépensé", 
        "Inscription"
      ];
      
      const tableRows = filteredClients.map(client => [
        `${client.prenom} ${client.nom}`,
        client.email,
        client.telephone,
        client.adresse,
        client.statut,
        client.niveauFidelite,
        getClientReservations(client.id).length,
        formatCurrencyForPDF(client.totalDepense),
        new Date(client.dateInscription).toLocaleDateString('fr-FR')
      ]);
      
      doc.autoTable({
        startY: doc.lastAutoTable.finalY + 20,
        head: [tableColumn],
        body: tableRows,
        theme: 'grid',
        headStyles: { fillColor: secondaryColor, textColor: [255, 255, 255], fontStyle: 'bold' },
        styles: { fontSize: 9, cellPadding: 3 },
        columnStyles: {
          0: { cellWidth: 40 },
          1: { cellWidth: 45 },
          2: { cellWidth: 30 },
          3: { cellWidth: 40 },
          4: { cellWidth: 20 },
          5: { cellWidth: 20 },
          6: { cellWidth: 25 },
          7: { cellWidth: 30 },
          8: { cellWidth: 25 }
        },
        margin: { left: margin, right: margin },
        tableWidth: 'auto',
        alternateRowStyles: { fillColor: [245, 247, 250] },
        didDrawPage: function (data) {
          // Add header
          doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
          doc.rect(0, 0, pageWidth, 25, 'F');
          
          // Add agency logo and name in header
          if (settings.agencyLogo) {
            try {
              // Add the agency logo from base64 string
              doc.addImage(settings.agencyLogo, 'PNG', margin, 5, 20, 15, undefined, 'FAST');
              
              // Add agency name
              doc.setTextColor(255, 255, 255);
              doc.setFontSize(12);
              doc.setFont('helvetica', 'bold');
              doc.text(settings.agencyName || 'Agence de Voyage', margin + 25, 15);
            } catch (logoError) {
              console.error('Error adding logo to PDF:', logoError);
              // Fallback to text only
              doc.setTextColor(255, 255, 255);
              doc.setFontSize(12);
              doc.setFont('helvetica', 'bold');
              doc.text(settings.agencyName || 'Agence de Voyage', margin, 15);
            }
          } else {
            // No logo, just add agency name
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(12);
            doc.setFont('helvetica', 'bold');
            doc.text(settings.agencyName || 'Agence de Voyage', margin, 15);
          }
          
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(16);
          doc.setFont('helvetica', 'bold');
          doc.text("Liste des Clients", pageWidth - margin, 17, { align: 'right' });
          
          // Add footer
          const pageNumber = doc.internal.getNumberOfPages();
          doc.setFontSize(10);
          doc.setTextColor(100, 100, 100);
          doc.text(`Page ${pageNumber}`, margin, pageHeight - 10);
          doc.text(`${settings.appName} - Système de Gestion pour Agence de Voyage`, pageWidth / 2, pageHeight - 10, { align: 'center' });
          doc.text(`Généré le: ${new Date().toLocaleDateString('fr-FR')}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
        }
      });
      
      // Save the PDF
      doc.save('liste-clients.pdf');
      
      // Show success message
      setSnackbar({
        open: true,
        message: 'Export PDF réussi',
        severity: 'success'
      });
    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de l\'export PDF',
        severity: 'error'
      });
    }
  };

  // Define LoadingIndicator component
  const LoadingIndicator = () => (
    <Box sx={{
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      zIndex: 9999,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 2
    }}>
      <CircularProgress color="primary" size={60} />
      <Typography variant="h6" sx={{ color: 'rgba(51,65,85,0.8)' }}>
        Chargement des données...
      </Typography>
    </Box>
  );

  // Define EnhancedStatCard component
  const EnhancedStatCard = ({ title, value, icon, gradient, delay, onClick, subtitle, chart, trend, progress }) => (
    <Grow in={true} style={{ transformOrigin: '0 0 0' }} timeout={1000}>
      <Card
        onClick={onClick}
        sx={{
          height: '100%',
          background: gradient ? `linear-gradient(135deg, ${gradient[0]}, ${gradient[1]})` : 'white',
          color: gradient ? 'white' : 'inherit',
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          transition: 'transform 0.3s, box-shadow 0.3s',
          cursor: onClick ? 'pointer' : 'default',
          '&:hover': onClick ? {
            transform: 'translateY(-5px)',
            boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
          } : {},
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <CardContent sx={{ flexGrow: 1, p: { xs: 2, md: 3 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box>
              <Typography variant="body2" sx={{ 
                opacity: gradient ? 0.9 : 0.7, 
                mb: 0.5, 
                fontWeight: 500,
                fontSize: { xs: '0.8rem', md: '0.875rem' }
              }}>
                {title}
              </Typography>
              <Typography variant="h5" sx={{ 
                fontWeight: 700,
                fontSize: { xs: '1.5rem', md: '1.75rem' }
              }}>
                {value}
              </Typography>
              {subtitle && (
                <Typography variant="body2" sx={{ 
                  opacity: gradient ? 0.9 : 0.7, 
                  mt: 0.5,
                  fontSize: { xs: '0.75rem', md: '0.875rem' }
                }}>
                  {subtitle}
                </Typography>
              )}
              {trend && (
                <Chip 
                  label={trend} 
                  size="small" 
                  sx={{ 
                    mt: 1,
                    backgroundColor: trend.startsWith('+') ? 
                      (gradient ? 'rgba(255,255,255,0.3)' : '#059669') : 
                      (gradient ? 'rgba(255,255,255,0.3)' : '#dc2626'),
                    color: gradient ? 'white' : 'white',
                    fontWeight: 600,
                    fontSize: { xs: '0.65rem', md: '0.75rem' },
                    height: { xs: 20, md: 24 }
                  }}
                />
              )}
            </Box>
            <Avatar
              sx={{
                bgcolor: gradient ? 'rgba(255,255,255,0.2)' : 'primary.light',
                color: gradient ? 'white' : 'primary.main',
                width: { xs: 40, md: 48 },
                height: { xs: 40, md: 48 }
              }}
            >
              {icon}
            </Avatar>
          </Box>
          
          {progress && (
            <Box sx={{ mt: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption" sx={{ opacity: gradient ? 0.9 : 0.7 }}>
                  Progression
                </Typography>
                <Typography variant="caption" sx={{ opacity: gradient ? 0.9 : 0.7 }}>
                  {progress}%
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={progress} 
                sx={{ 
                  height: 6, 
                  borderRadius: 3,
                  backgroundColor: gradient ? 'rgba(255,255,255,0.2)' : 'rgba(99,102,241,0.2)',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: gradient ? 'white' : 'primary.main',
                  }
                }} 
              />
            </Box>
          )}
          
          {chart && (
            <Box sx={{ mt: 2, height: { xs: 40, md: 60 } }}>
              {chart}
            </Box>
          )}
        </CardContent>
      </Card>
    </Grow>
  );

  // Define FloatingActionCard component
  const FloatingActionCard = ({ title, icon, color, action, description, badge, onClick }) => {
    // Get theme and check if mobile
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    
    return (
      <Card sx={{
        background: `linear-gradient(135deg, ${color}15, white)`,
        border: `1px solid ${color}30`,
        borderRadius: { xs: 3, md: 4 },
        overflow: 'hidden',
        position: 'relative',
        cursor: 'pointer',
        height: '100%', // Ensure consistent height
      }} onClick={onClick}>
        <CardContent sx={{ 
          p: { xs: 2, sm: 2.5, md: 3 }, 
          position: 'relative', 
          zIndex: 1,
          height: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: { xs: 1, md: 2 }, 
            mb: { xs: 1.5, md: 2 },
            flexWrap: 'wrap'
          }}>
            <Avatar className="action-icon" sx={{ 
              background: `linear-gradient(135deg, ${color}, ${color}cc)`,
              width: { xs: 48, sm: 52, md: 56 },
              height: { xs: 48, sm: 52, md: 56 },
              boxShadow: `0 8px 25px ${color}40`,
              '& .MuiSvgIcon-root': {
                fontSize: { xs: 26, sm: 28, md: 30 }
              }
            }}>
              {icon}
            </Avatar>
            {badge && (
              <Chip 
                label={badge} 
                size="small" 
                sx={{ 
                  background: `linear-gradient(135deg, ${color}, ${color}dd)`,
                  color: 'white',
                  fontWeight: 600,
                  height: { xs: '20px', md: '24px' },
                  fontSize: { xs: '0.7rem', md: '0.75rem' }
                }}
              />
            )}
          </Box>
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 700, 
              mb: { xs: 0.5, md: 1 }, 
              color: 'rgba(51,65,85,0.9)',
              lineHeight: 1.3,
              fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.3rem' }
            }}
          >
            {title}
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'rgba(51,65,85,0.7)',
              fontSize: { xs: '0.75rem', md: '0.875rem' },
              display: { xs: '-webkit-box', md: 'block' },
              WebkitLineClamp: { xs: 2, md: 'none' },
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {description}
          </Typography>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ p: { xs: 0, md: 3 } }} className="space-y-6">
      {/* Header Section */}
      <Box sx={{ 
        mb: { xs: 2, md: 4 },
        p: { xs: 2, md: 0 }
      }} className="flex justify-between items-center">
        <div>
          <Typography variant="h3" sx={{ 
            fontWeight: 800, 
            color: 'white',
            mb: 1.5,
            fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.5rem' },
            letterSpacing: '-0.01em'
          }}>
            Gestion des Clients
          </Typography>
          <Typography variant="body1" sx={{ 
            color: 'rgba(255,255,255,0.6)',
            fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' }
          }}>
            Gérez votre base de données clients et suivez leurs activités
          </Typography>
        </div>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{
            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
            color: 'white',
            px: 3,
            py: 1.5,
            borderRadius: 3,
            fontWeight: 600,
            '&:hover': {
              background: 'linear-gradient(135deg, #4f46e5, #7c3aed)'
            },
            textTransform: 'none',
            boxShadow: '0 4px 15px rgba(99, 102, 241, 0.4)'
          }}
        >
          Nouveau Client
        </Button>
      </Box>

      {/* Loading Indicator */}
      {isLoading ? (
        <LoadingIndicator />
      ) : (
        <>
          {/* Quick Actions Section (hidden if showQuickActions is false) */}
          {showQuickActions && (
          <Box sx={{ mb: { xs: 3, md: 4 }, px: { xs: 2, md: 0 } }} className="space-y-2">
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <PeopleIcon sx={{ mr: 1.5, color: '#6366f1', fontSize: { xs: 26, md: 30 } }} />
              <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: '1.4rem', md: '1.6rem' }, color: 'white' }}>
                Actions Rapides
              </Typography>
            </Box>
            
            <Grid container spacing={{ xs: 2, md: 3 }}>
              <Grid item xs={6} sm={6} md={3}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                >
                  <Card sx={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: 4,
                    cursor: 'pointer',
                    transition: 'transform 0.3s, box-shadow 0.3s',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 10px 25px rgba(99, 102, 241, 0.4)'
                    },
                    height: '100%'
                  }} onClick={() => handleOpenDialog()}>
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                        <Avatar sx={{
                          bgcolor: '#6366f1',
                          width: 56,
                          height: 56,
                          mb: 2
                        }}>
                          <AddIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                        </Avatar>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: 'white', mb: 1 }}>
                          Nouveau Client
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                          Ajouter un client au système
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
              <Grid item xs={6} sm={6} md={3}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <Card sx={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: 4,
                    cursor: 'pointer',
                    transition: 'transform 0.3s, box-shadow 0.3s',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 10px 25px rgba(16, 185, 129, 0.4)'
                    },
                    height: '100%'
                  }} onClick={() => {}}>
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                        <Avatar sx={{
                          bgcolor: '#10b981',
                          width: 56,
                          height: 56,
                          mb: 2
                        }}>
                          <ExportIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                        </Avatar>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: 'white', mb: 1 }}>
                          Importer Clients
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                          Importer des clients depuis un fichier
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
              <Grid item xs={6} sm={6} md={3}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <Card sx={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: 4,
                    cursor: 'pointer',
                    transition: 'transform 0.3s, box-shadow 0.3s',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 10px 25px rgba(245, 158, 11, 0.4)'
                    },
                    height: '100%'
                  }} onClick={() => exportClientsToPDF()}>
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                        <Avatar sx={{
                          bgcolor: '#f59e0b',
                          width: 56,
                          height: 56,
                          mb: 2
                        }}>
                          <ExportIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                        </Avatar>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: 'white', mb: 1 }}>
                          Exporter Clients
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                          Exporter la liste des clients
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
              <Grid item xs={6} sm={6} md={3}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  <Card sx={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: 4,
                    cursor: 'pointer',
                    transition: 'transform 0.3s, box-shadow 0.3s',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 10px 25px rgba(139, 92, 246, 0.4)'
                    },
                    height: '100%'
                  }} onClick={() => navigate('/rapports')}>
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                        <Avatar sx={{
                          bgcolor: '#8b5cf6',
                          width: 56,
                          height: 56,
                          mb: 2
                        }}>
                          <BarChart />
                        </Avatar>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: 'white', mb: 1 }}>
                          Statistiques
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                          Analyser les données clients
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            </Grid>
          </Box>
          )}
          
          {/* Key Metrics Section */}
          <Box sx={{ mb: { xs: 3, md: 4 }, px: { xs: 2, md: 0 } }} className="space-y-2">
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <AssessmentIcon sx={{ mr: 1.5, color: '#10b981', fontSize: { xs: 26, md: 30 } }} />
              <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: '1.4rem', md: '1.6rem' }, color: 'white' }}>
                Métriques Clients
              </Typography>
            </Box>
            
            <Grid container spacing={{ xs: 2, md: 3 }}>
              {statsCards.map((stat, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white/60 text-sm font-medium">{stat.title}</p>
                        <p className="text-2xl font-bold text-white mt-2">{stat.value}</p>
                        <p className="text-white/60 text-xs mt-1">
                          {index === 0 ? "Base clients totale" : 
                           index === 1 ? "Clients actifs" : 
                           index === 2 ? "Nouveaux ce mois" : 
                           "Revenus générés"}
                        </p>
                        {stat.trend && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-white/20 text-white mt-2">
                            {stat.trend}
                          </span>
                        )}
                      </div>
                      <div className={`p-3 rounded-2xl ${index === 0 ? 'bg-gradient-to-r from-blue-600 to-blue-700' : 
                                      index === 1 ? 'bg-gradient-to-r from-green-600 to-green-700' : 
                                      index === 2 ? 'bg-gradient-to-r from-purple-600 to-purple-700' : 
                                      'bg-gradient-to-r from-amber-600 to-amber-700'}`}>
                        <stat.icon className="h-6 w-6 text-white" />
                      </div>
                    </div>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Filtration Rapide */}
          <Box sx={{ mb: { xs: 3, md: 4 }, px: { xs: 2, md: 0 } }} className="space-y-2">
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <FilterIcon sx={{ mr: 1.5, color: '#8b5cf6', fontSize: { xs: 26, md: 30 } }} />
              <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: '1.4rem', md: '1.6rem' }, color: 'white' }}>
                Filtration Rapide
              </Typography>
            </Box>
            
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
              <div className="flex space-x-1 overflow-x-auto hide-scrollbar">
                <button
                  onClick={() => setActiveTab('all')}
                  className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                    activeTab === 'all'
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                      : 'text-white/80 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <PeopleIcon sx={{ fontSize: 20, color: 'rgba(255, 255, 255, 0.7)' }} />
                  <span className="hidden sm:inline whitespace-nowrap">Tous les Clients</span>
                </button>
                <button
                  onClick={() => setActiveTab('actif')}
                  className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                    activeTab === 'actif'
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                      : 'text-white/80 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <CheckCircleIcon sx={{ fontSize: 20, color: 'rgba(255, 255, 255, 0.7)' }} />
                  <span className="hidden sm:inline whitespace-nowrap">Clients Actifs</span>
                </button>
                <button
                  onClick={() => setActiveTab('inactif')}
                  className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                    activeTab === 'inactif'
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                      : 'text-white/80 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <XCircleIcon sx={{ fontSize: 20, color: 'rgba(255, 255, 255, 0.7)' }} />
                  <span className="hidden sm:inline whitespace-nowrap">Clients Inactifs</span>
                </button>
                <button
                  onClick={() => setActiveTab('premium')}
                  className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                    activeTab === 'premium'
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                      : 'text-white/80 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <StarIcon className="h-5 w-5" />
                  <span className="hidden sm:inline whitespace-nowrap">Premium</span>
                </button>
                <button
                  onClick={() => setActiveTab('standard')}
                  className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                    activeTab === 'standard'
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                      : 'text-white/80 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <TrendingUpIcon className="h-5 w-5" />
                  <span className="hidden sm:inline whitespace-nowrap">Standard</span>
                </button>
                <button
                  onClick={() => setActiveTab('bronze')}
                  className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                    activeTab === 'bronze'
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                      : 'text-white/80 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <AssessmentIcon className="h-5 w-5" />
                  <span className="hidden sm:inline whitespace-nowrap">Bronze</span>
                </button>
              </div>
            </div>
          </Box>

          {/* Search and Filters */}
          <Box sx={{ mb: { xs: 3, md: 4 }, px: { xs: 2, md: 0 } }} className="space-y-2">
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <SearchIcon sx={{ mr: 1.5, color: '#f59e0b', fontSize: { xs: 26, md: 30 } }} />
              <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: '1.4rem', md: '1.6rem' }, color: 'white' }}>
                Rechercher des Clients
              </Typography>
            </Box>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
            >
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    placeholder="Rechercher un client..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        color: 'white',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.2)',
                      },
                      '& .MuiInputBase-input::placeholder': {
                        color: 'rgba(255, 255, 255, 0.5)',
                        opacity: 1,
                      },
                      '& .MuiInputBase-input': {
                        color: 'white',
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel sx={{ 
                      color: 'rgba(255, 255, 255, 0.7)',
                      '&.Mui-focused': {
                        color: 'rgba(255, 255, 255, 0.9)'
                      }
                    }}>Statut</InputLabel>
                    <Select
                      value={filterStatus}
                      label="Statut"
                      onChange={(e) => setFilterStatus(e.target.value)}
                      sx={{ 
                        borderRadius: 2,
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        color: 'white',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(255, 255, 255, 0.2)',
                        },
                        '& .MuiSvgIcon-root': {
                          color: 'rgba(255, 255, 255, 0.7)',
                        },
                      }}
                    >
                      <MenuItem value="all">Tous</MenuItem>
                      <MenuItem value="Actif">Actifs</MenuItem>
                      <MenuItem value="Inactif">Inactifs</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      startIcon={<FilterIcon />}
                      sx={{ 
                        borderRadius: 2, 
                        flex: 1,
                        borderColor: 'rgba(255, 255, 255, 0.3)',
                        color: 'white',
                        '&:hover': {
                          borderColor: 'rgba(255, 255, 255, 0.5)',
                          backgroundColor: 'rgba(255, 255, 255, 0.1)'
                        }
                      }}
                    >
                      Filtrer
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<SearchIcon />}
                      sx={{ 
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                        flex: 1,
                        '&:hover': {
                          background: 'linear-gradient(135deg, #d97706, #b45309)'
                        },
                        boxShadow: '0 4px 15px rgba(245, 158, 11, 0.4)'
                      }}
                    >
                      Rechercher
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </motion.div>
          </Box>

          {/* Clients Table Section */}
          <Grid container spacing={{ xs: 2, md: 3 }} sx={{ px: { xs: 2, md: 0 } }}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PeopleIcon sx={{ mr: 1.5, color: '#8b5cf6', fontSize: { xs: 26, md: 30 } }} />
                <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: '1.4rem', md: '1.6rem' }, color: 'white' }}>
                  Liste des Clients
                </Typography>
                <Box sx={{ ml: 'auto', display: 'flex', gap: 1, alignItems: 'center' }}>
                  {/* View Toggle Buttons */}
                  <Box sx={{ display: 'flex', border: '1px solid rgba(255, 255, 255, 0.3)', borderRadius: 2, overflow: 'hidden' }}>
                    <Button
                      variant={viewMode === 'table' ? 'contained' : 'outlined'}
                      onClick={() => setViewMode('table')}
                      sx={{
                        minWidth: 'auto',
                        px: 2,
                        py: 1,
                        borderRadius: 0,
                        border: 'none',
                        backgroundColor: viewMode === 'table' ? 'rgba(139, 92, 246, 0.8)' : 'transparent',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: viewMode === 'table' ? 'rgba(139, 92, 246, 1)' : 'rgba(255, 255, 255, 0.1)',
                          border: 'none'
                        }
                      }}
                    >
                      <TableViewIcon fontSize="small" />
                    </Button>
                    <Button
                      variant={viewMode === 'card' ? 'contained' : 'outlined'}
                      onClick={() => setViewMode('card')}
                      sx={{
                        minWidth: 'auto',
                        px: 2,
                        py: 1,
                        borderRadius: 0,
                        border: 'none',
                        backgroundColor: viewMode === 'card' ? 'rgba(139, 92, 246, 0.8)' : 'transparent',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: viewMode === 'card' ? 'rgba(139, 92, 246, 1)' : 'rgba(255, 255, 255, 0.1)',
                          border: 'none'
                        }
                      }}
                    >
                      <CardViewIcon fontSize="small" />
                    </Button>
                  </Box>
                  <Button
                    variant="outlined"
                    startIcon={<ExportIcon />}
                    onClick={exportClientsToPDF}
                    sx={{ 
                      borderRadius: 2,
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                      color: 'white',
                      '&:hover': {
                        borderColor: 'rgba(255, 255, 255, 0.5)',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)'
                      }
                    }}
                  >
                    Exporter PDF
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog()}
                    sx={{ 
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #7c3aed, #5b21b6)'
                      },
                      boxShadow: '0 4px 15px rgba(139, 92, 246, 0.4)'
                    }}
                  >
                    Nouveau Client
                  </Button>
                </Box>
              </Box>
            </Grid>
            
            <Grid item xs={12}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 overflow-hidden relative">
                {/* Table View */}
                {viewMode === 'table' && (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-white/5">
                      <tr>
                        <th className="px-6 py-4 text-left text-white font-semibold">Client</th>
                        <th className="px-6 py-4 text-left text-white font-semibold">Contact</th>
                        <th className="px-6 py-4 text-left text-white font-semibold">Localisation</th>
                        <th className="px-6 py-4 text-left text-white font-semibold">Statut</th>
                        <th className="px-6 py-4 text-left text-white font-semibold">Fidélité</th>
                        <th className="px-6 py-4 text-left text-white font-semibold">Réservations</th>
                        <th className="px-6 py-4 text-left text-white font-semibold">Total Dépensé</th>
                        <th className="px-6 py-4 text-left text-white font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/10">
                      {isLoading ? (
                        // Show skeleton rows while loading
                        [...Array(5)].map((_, index) => (
                          <tr key={`skeleton-${index}`}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
                                <Box>
                                  <Skeleton variant="text" width={120} />
                                  <Skeleton variant="text" width={80} />
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <Skeleton variant="text" width={150} />
                                <Skeleton variant="text" width={100} />
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Skeleton variant="text" width={120} />
                            </TableCell>
                            <TableCell>
                              <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: 16 }} />
                            </TableCell>
                            <TableCell>
                              <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: 16 }} />
                            </TableCell>
                            <TableCell>
                              <Skeleton variant="text" width={80} />
                            </TableCell>
                            <TableCell>
                              <Skeleton variant="text" width={80} />
                            </TableCell>
                            <TableCell>
                              <Skeleton variant="circular" width={30} height={30} />
                            </TableCell>
                          </tr>
                        ))
                      ) : filteredClients.length === 0 ? (
                        <tr>
                          <td colSpan={8} className="px-6 py-12 text-center">
                            <div className="flex flex-col items-center justify-center p-12 text-center">
                              <PersonIcon className="h-16 w-16 text-white/30 mb-4" />
                              <h3 className="text-xl font-semibold text-white mb-2">Aucun client trouvé</h3>
                              <p className="text-white/60 mb-6">Aucun résultat ne correspond à vos critères de recherche.</p>
                              <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => handleOpenDialog()}
                                sx={{ 
                                  borderRadius: 2,
                                  background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
                                  '&:hover': {
                                    background: 'linear-gradient(135deg, #7c3aed, #5b21b6)'
                                  },
                                }}
                              >
                                Ajouter un client
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        filteredClients
                          .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                          .map((client) => (
                                                                            <motion.tr
                              key={client.id}
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              className="hover:bg-white/5 transition-colors"
                            >
                              <td className="px-6 py-4">
                                <div className="flex items-center">
                                  <div className="h-10 w-10 rounded-full bg-blue-600 text-white flex items-center justify-center mr-3 text-sm font-semibold shadow-md">
                                    {getInitials(client.nom, client.prenom)}
                                  </div>
                                  <div>
                                    <div className="font-medium text-white">{client.prenom} {client.nom}</div>
                                    <div className="text-xs text-white/70">Inscrit le {client.dateInscription}</div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex flex-col space-y-1">
                                  <div className="flex items-center text-white">
                                    <EmailIcon className="h-4 w-4 text-white/60 mr-1.5" />
                                    <span className="text-sm truncate max-w-[180px]">{client.email}</span>
                                  </div>
                                  <div className="flex items-center text-white">
                                    <PhoneIcon className="h-4 w-4 text-white/60 mr-1.5" />
                                    <span className="text-sm">{client.telephone}</span>
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex items-center text-white">
                                  <LocationIcon className="h-4 w-4 text-white/60 mr-1.5" />
                                  <span className="text-sm">
                                    {client.ville && client.pays 
                                      ? `${client.ville}, ${client.pays}`
                                      : client.ville || client.pays || client.adresse || "Non spécifiée"}
                                  </span>
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                  client.statut === 'Actif' 
                                    ? 'bg-green-500/20 text-green-400' 
                                    : 'bg-gray-500/20 text-gray-400'
                                }`}>
                                  {client.statut === 'Actif' ? 'Actif' : 'Inactif'}
                                </span>
                              </td>
                              <td className="px-6 py-4">
                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                  client.niveauFidelite === 'Premium' 
                                    ? 'bg-red-500/20 text-red-400' 
                                    : client.niveauFidelite === 'Standard'
                                    ? 'bg-blue-500/20 text-blue-400'
                                    : 'bg-yellow-500/20 text-yellow-400'
                                }`}>
                                  {client.niveauFidelite}
                                </span>
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex items-center space-x-2">
                                  <div className="h-6 w-6 rounded-full bg-blue-500/20 flex items-center justify-center">
                                    <FlightIcon className="h-4 w-4 text-blue-400" />
                                  </div>
                                  <span className="text-white font-medium">
                                    {client.nombreReservations === 1 ? '1 voyage' : `${client.nombreReservations} voyages`}
                                  </span>
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <div className="text-white font-medium">
                                  {formatCurrency(client.totalDepense)} DA
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex items-center space-x-2">
                                  <button
                                    onClick={() => { setSelectedClient(client); handleViewDetails(); }}
                                    className="p-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors"
                                    title="Voir les détails"
                                  >
                                    <VisibilityIcon className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => handleOpenDialog(client)}
                                    className="p-2 bg-yellow-500/20 text-yellow-400 rounded-lg hover:bg-yellow-500/30 transition-colors"
                                    title="Modifier"
                                  >
                                    <EditIcon className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteClient(client.id)}
                                    className="p-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
                                    title="Supprimer"
                                  >
                                    <DeleteIcon className="h-4 w-4" />
                                  </button>
                                </div>
                              </td>
                            </motion.tr>
                          ))
                      )}
                    </tbody>
                  </table>
                </div>
                )}

                {/* Card View */}
                {viewMode === 'card' && (
                  <div className="p-6">
                    {isLoading ? (
                      // Show skeleton cards while loading
                      <Grid container spacing={3}>
                        {[...Array(6)].map((_, index) => (
                          <Grid item xs={12} sm={6} lg={4} key={`skeleton-card-${index}`}>
                            <Card sx={{ 
                              background: 'rgba(255, 255, 255, 0.05)',
                              backdropFilter: 'blur(10px)',
                              border: '1px solid rgba(255, 255, 255, 0.1)',
                              borderRadius: 3,
                              p: 2,
                              height: 280
                            }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Skeleton variant="circular" width={60} height={60} />
                                <Box sx={{ ml: 2, flex: 1 }}>
                                  <Skeleton variant="text" width="80%" height={24} />
                                  <Skeleton variant="text" width="60%" height={20} />
                                </Box>
                              </Box>
                              <Box sx={{ mb: 2 }}>
                                <Skeleton variant="text" width="100%" height={20} />
                                <Skeleton variant="text" width="90%" height={20} />
                                <Skeleton variant="text" width="70%" height={20} />
                              </Box>
                              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: 16 }} />
                                <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: 16 }} />
                              </Box>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
                                <Skeleton variant="text" width={100} height={24} />
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <Skeleton variant="circular" width={32} height={32} />
                                  <Skeleton variant="circular" width={32} height={32} />
                                  <Skeleton variant="circular" width={32} height={32} />
                                </Box>
                              </Box>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    ) : paginatedClients.length === 0 ? (
                      <div className="text-center py-12">
                        <div className="flex flex-col items-center">
                          <PeopleIcon className="h-16 w-16 text-white/40 mb-4" />
                          <h3 className="text-xl font-medium text-white/60 mb-2">Aucun client trouvé</h3>
                          <p className="text-white/40 text-lg">
                            {searchTerm || filterStatus !== 'all' 
                              ? 'Essayez de modifier vos critères de recherche.' 
                              : 'Commencez par ajouter votre premier client.'}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <Grid container spacing={3}>
                        {paginatedClients.map((client, index) => (
                          <Grid item xs={12} sm={6} lg={4} key={client.id}>
                            <motion.div
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: index * 0.05 }}
                            >
                              <Card sx={{ 
                                background: 'rgba(255, 255, 255, 0.05)',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                borderRadius: 3,
                                p: 3,
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                  transform: 'translateY(-4px)',
                                  boxShadow: '0 12px 24px rgba(139, 92, 246, 0.2)',
                                  background: 'rgba(255, 255, 255, 0.08)'
                                }
                              }}>
                                {/* Client Header */}
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                  <Avatar
                                    sx={{
                                      width: 60,
                                      height: 60,
                                      background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
                                      fontSize: '1.5rem',
                                      fontWeight: 600,
                                      mr: 2
                                    }}
                                  >
                                    {client.nom ? client.nom.charAt(0).toUpperCase() : 'N'}
                                  </Avatar>
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="h6" sx={{ 
                                      color: 'white', 
                                      fontWeight: 600, 
                                      fontSize: '1.1rem',
                                      lineHeight: 1.2,
                                      mb: 0.5
                                    }}>
                                      {client.nom || 'Nom non renseigné'}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                                      Inscrit le {formatDate(client.dateInscription)}
                                    </Typography>
                                  </Box>
                                </Box>

                                {/* Contact Info */}
                                <Box sx={{ mb: 3, flex: 1 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                                    <EmailIcon sx={{ fontSize: 16, color: 'rgba(255, 255, 255, 0.6)', mr: 1 }} />
                                    <Typography variant="body2" sx={{ 
                                      color: 'rgba(255, 255, 255, 0.8)', 
                                      fontSize: '0.85rem',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap'
                                    }}>
                                      {client.email || 'Email non renseigné'}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                                    <PhoneIcon sx={{ fontSize: 16, color: 'rgba(255, 255, 255, 0.6)', mr: 1 }} />
                                    <Typography variant="body2" sx={{ 
                                      color: 'rgba(255, 255, 255, 0.8)', 
                                      fontSize: '0.85rem' 
                                    }}>
                                      {client.telephone || 'Téléphone non renseigné'}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <LocationIcon sx={{ fontSize: 16, color: 'rgba(255, 255, 255, 0.6)', mr: 1 }} />
                                    <Typography variant="body2" sx={{ 
                                      color: 'rgba(255, 255, 255, 0.8)', 
                                      fontSize: '0.85rem' 
                                    }}>
                                      {client.ville || 'Ville non renseignée'}, {client.pays || 'Pays non renseigné'}
                                    </Typography>
                                  </Box>
                                </Box>

                                {/* Status and Loyalty Badges */}
                                <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
                                  <Chip
                                    label={client.statut || 'Inactif'}
                                    size="small"
                                    sx={{
                                      backgroundColor: getStatusColor(client.statut),
                                      color: 'white',
                                      fontWeight: 500,
                                      fontSize: '0.75rem'
                                    }}
                                  />
                                  <Chip
                                    label={client.niveauFidelite || 'Standard'}
                                    size="small"
                                    sx={{
                                      backgroundColor: getFidelityColor(client.niveauFidelite),
                                      color: 'white',
                                      fontWeight: 500,
                                      fontSize: '0.75rem'
                                    }}
                                  />
                                </Box>

                                {/* Stats */}
                                <Box sx={{ mb: 3 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <FlightIcon sx={{ fontSize: 16, color: 'rgba(255, 255, 255, 0.6)', mr: 1 }} />
                                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.85rem' }}>
                                      {getClientReservationsCount(client.id)} voyages
                                    </Typography>
                                  </Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <PaymentIcon sx={{ fontSize: 16, color: 'rgba(255, 255, 255, 0.6)', mr: 1 }} />
                                    <Typography variant="body2" sx={{ 
                                      color: 'white', 
                                      fontSize: '0.9rem', 
                                      fontWeight: 600 
                                    }}>
                                      {formatCurrency(client.totalDepense || 0)}
                                    </Typography>
                                  </Box>
                                </Box>

                                {/* Actions */}
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 'auto' }}>
                                  <Tooltip title="Voir les détails">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleViewClient(client)}
                                      sx={{
                                        color: 'rgb(34, 197, 94)',
                                        bgcolor: 'rgba(34, 197, 94, 0.1)',
                                        '&:hover': { bgcolor: 'rgba(34, 197, 94, 0.2)' },
                                        width: 36,
                                        height: 36
                                      }}
                                    >
                                      <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Modifier">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleEditClient(client)}
                                      sx={{
                                        color: 'rgb(245, 158, 11)',
                                        bgcolor: 'rgba(245, 158, 11, 0.1)',
                                        '&:hover': { bgcolor: 'rgba(245, 158, 11, 0.2)' },
                                        width: 36,
                                        height: 36
                                      }}
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Supprimer">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleDeleteClient(client)}
                                      sx={{
                                        color: 'rgb(239, 68, 68)',
                                        bgcolor: 'rgba(239, 68, 68, 0.1)',
                                        '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.2)' },
                                        width: 36,
                                        height: 36
                                      }}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </Card>
                            </motion.div>
                          </Grid>
                        ))}
                      </Grid>
                    )}
                  </div>
                )}
                  <div className="px-6 py-4 flex items-center justify-between border-t border-white/10">
                    <div className="text-sm text-white/60">
                      Affichage de {(page * rowsPerPage) + 1} à {Math.min((page + 1) * rowsPerPage, filteredClients.length)} sur {filteredClients.length} clients
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setPage(Math.max(0, page - 1))}
                        disabled={page === 0}
                        className="px-3 py-1 bg-white/10 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Précédent
                      </button>
                      <button
                        onClick={() => setPage(Math.min(Math.ceil(filteredClients.length / rowsPerPage) - 1, page + 1))}
                        disabled={page >= Math.ceil(filteredClients.length / rowsPerPage) - 1}
                        className="px-3 py-1 bg-white/10 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Suivant
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            </Grid>
          </Grid>
        </>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }
        }}
      >
        <MenuListItem onClick={() => { handleOpenDialog(selectedClient); handleMenuClose(); }}>
          <ListItemIcon>
            <EditIcon fontSize="small" sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
          </ListItemIcon>
          <ListItemText>Modifier</ListItemText>
        </MenuListItem>
        <MenuListItem onClick={handleViewDetails}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
          </ListItemIcon>
          <ListItemText>Voir détails</ListItemText>
        </MenuListItem>
        <MenuListItem onClick={() => { handleDeleteClient(selectedClient?.id); handleMenuClose(); }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
          </ListItemIcon>
          <ListItemText sx={{ color: 'error.main' }}>Supprimer</ListItemText>
        </MenuListItem>
      </Menu>

      {/* Add/Edit Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            maxWidth: 600,
            width: '100%'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: 'white' }}>
            {editingClient ? 'Modifier le client' : 'Ajouter un nouveau client'}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nom"
                value={formData.nom}
                onChange={(e) => setFormData({...formData, nom: e.target.value})}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white'
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.2)'
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)'
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: 'rgba(255, 255, 255, 0.5)',
                    opacity: 1
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Prénom"
                value={formData.prenom}
                onChange={(e) => setFormData({...formData, prenom: e.target.value})}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white'
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.2)'
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)'
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: 'rgba(255, 255, 255, 0.5)',
                    opacity: 1
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white'
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.2)'
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)'
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: 'rgba(255, 255, 255, 0.5)',
                    opacity: 1
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Téléphone"
                value={formData.telephone}
                onChange={(e) => setFormData({...formData, telephone: e.target.value})}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white'
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.2)'
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)'
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: 'rgba(255, 255, 255, 0.5)',
                    opacity: 1
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Adresse"
                value={formData.adresse}
                onChange={(e) => setFormData({...formData, adresse: e.target.value})}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white'
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.2)'
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)'
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: 'rgba(255, 255, 255, 0.5)',
                    opacity: 1
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Ville"
                value={formData.ville}
                onChange={(e) => setFormData({...formData, ville: e.target.value})}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white'
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.2)'
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)'
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: 'rgba(255, 255, 255, 0.5)',
                    opacity: 1
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Pays"
                value={formData.pays}
                onChange={(e) => setFormData({...formData, pays: e.target.value})}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white'
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.2)'
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)'
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: 'rgba(255, 255, 255, 0.5)',
                    opacity: 1
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
                              <FormControl fullWidth>
                <InputLabel sx={{ 
                  color: 'rgba(255, 255, 255, 0.7)',
                  '&.Mui-focused': {
                    color: 'rgba(255, 255, 255, 0.9)'
                  }
                }}>Statut</InputLabel>
                <Select
                  value={formData.statut}
                  label="Statut"
                  onChange={(e) => setFormData({...formData, statut: e.target.value})}
                  sx={{ 
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.2)'
                    },
                    '& .MuiSvgIcon-root': {
                      color: 'rgba(255, 255, 255, 0.7)'
                    }
                  }}
                >
                  <MenuItem value="Actif">Actif</MenuItem>
                  <MenuItem value="Inactif">Inactif</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button 
            onClick={handleCloseDialog} 
            sx={{ borderRadius: 2, color: 'white' }}
          >
            Annuler
          </Button>
          <Button 
            onClick={handleSaveClient}
            variant="contained"
            sx={{ 
              borderRadius: 2,
              background: 'linear-gradient(135deg, #1e40af 0%, #7c3aed 100%)',
              color: 'white'
            }}
          >
            {editingClient ? 'Modifier' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Client Details Dialog with Interconnected Data */}
      <Dialog 
        open={openDetailsDialog} 
        onClose={() => setOpenDetailsDialog(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            minHeight: '600px'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1, color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar 
              sx={{ 
                bgcolor: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                width: 50,
                height: 50
              }}
            >
              {selectedClient && getInitials(selectedClient.nom, selectedClient.prenom)}
            </Avatar>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, color: 'white' }}>
                {selectedClient && `${selectedClient.prenom} ${selectedClient.nom}`}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                {selectedClient && `Client depuis le ${formatDate(selectedClient.dateInscription)}`}
              </Typography>
            </Box>
            <Box sx={{ ml: 'auto' }}>
              <Chip
                label={selectedClient?.niveauFidelite}
                color={getFidelityColorForChip(selectedClient?.niveauFidelite)}
                sx={{ borderRadius: 2 }}
              />
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Tabs 
            value={detailsTab} 
            onChange={(e, newValue) => setDetailsTab(newValue)}
            sx={{ 
              borderBottom: 1, 
              borderColor: 'rgba(255, 255, 255, 0.2)', 
              px: 3,
              '& .MuiTab-root': {
                color: 'rgba(255, 255, 255, 0.7)',
                '&.Mui-selected': {
                  color: 'white'
                }
              },
              '& .MuiTabs-indicator': {
                backgroundColor: 'white'
              }
            }}
          >
            <Tab label="Informations Générales" />
            <Tab label="Réservations" />
            <Tab label="Transactions" />
            <Tab label="Statistiques" />
          </Tabs>

          {/* Tab Panel 0: General Information */}
          {detailsTab === 0 && (
            <Box sx={{ p: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card sx={{ p: 3, background: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)', borderRadius: 2 }}>
                    <Typography variant="h6" sx={{ mb: 2, color: 'white', fontWeight: 600 }}>Contact</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <EmailIcon sx={{ fontSize: 20, color: 'rgba(255, 255, 255, 0.7)' }} />
                      <Typography sx={{ color: 'white' }}>{selectedClient?.email}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <PhoneIcon sx={{ fontSize: 20, color: 'rgba(255, 255, 255, 0.7)' }} />
                      <Typography sx={{ color: 'white' }}>{selectedClient?.telephone}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocationIcon sx={{ fontSize: 20, color: 'rgba(255, 255, 255, 0.7)' }} />
                      <Typography sx={{ color: 'white' }}>{selectedClient?.adresse}</Typography>
                    </Box>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card sx={{ p: 3, background: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)', borderRadius: 2 }}>
                    <Typography variant="h6" sx={{ mb: 2, color: 'white', fontWeight: 600 }}>Préférences</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {selectedClient?.preferencesVoyage?.map((pref, index) => (
                        <Chip 
                          key={index}
                          label={pref}
                          size="small"
                          sx={{ 
                            borderRadius: 2,
                            backgroundColor: 'rgba(139, 92, 246, 0.2)',
                            color: '#c084fc',
                            border: '1px solid rgba(139, 92, 246, 0.3)'
                          }}
                        />
                      ))}
                    </Box>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Tab Panel 1: Reservations */}
          {detailsTab === 1 && (
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, color: 'white', fontWeight: 600 }}>
                Historique des Réservations ({getClientReservations(selectedClient?.id).length})
              </Typography>
              {getClientReservations(selectedClient?.id).length > 0 ? (
                <List>
                  {getClientReservations(selectedClient?.id).map((reservation, index) => (
                    <React.Fragment key={reservation.id}>
                      <ListItem 
                        sx={{ 
                          background: 'rgba(255, 255, 255, 0.1)',
                          borderRadius: 2,
                          mb: 1,
                          border: '1px solid rgba(255, 255, 255, 0.2)'
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar sx={{ 
                            background: 'linear-gradient(135deg, #10b981, #059669)',
                            color: 'white'
                          }}>
                            <FlightIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Typography sx={{ color: 'white', fontWeight: 600 }}>
                              {reservation.destination}
                            </Typography>
                          }
                          secondary={
                            <Box>
                              <Typography variant="caption" display="block" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                Du {formatDate(reservation.dateDepart)} au {formatDate(reservation.dateRetour)}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
                                <Chip 
                                  label={reservation.statut}
                                  size="small"
                                  sx={{ 
                                    borderRadius: 2,
                                    backgroundColor: reservation.statut === 'Confirmé' 
                                      ? 'rgba(34, 197, 94, 0.2)' 
                                      : 'rgba(245, 158, 11, 0.2)',
                                    color: reservation.statut === 'Confirmé' ? '#4ade80' : '#fbbf24',
                                    border: `1px solid ${reservation.statut === 'Confirmé' ? 'rgba(34, 197, 94, 0.3)' : 'rgba(245, 158, 11, 0.3)'}`
                                  }}
                                />
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'white' }}>
                                  {formatCurrency(reservation.prix)}
                                </Typography>
                              </Box>
                            </Box>
                          }
                        />
                      </ListItem>
                      {index < getClientReservations(selectedClient?.id).length - 1 && 
                        <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.2)', my: 1 }} />
                      }
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Card sx={{ p: 3, textAlign: 'center', background: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)', borderRadius: 2 }}>
                  <FlightIcon sx={{ fontSize: 48, color: 'rgba(255, 255, 255, 0.5)', mb: 2 }} />
                  <Typography variant="h6" sx={{ color: 'white', mb: 1 }}>Aucune réservation</Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Ce client n'a pas encore effectué de réservation.
                  </Typography>
                </Card>
              )}
            </Box>
          )}

          {/* Tab Panel 2: Transactions */}
          {detailsTab === 2 && (
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, color: 'white', fontWeight: 600 }}>
                Historique des Paiements ({getClientTransactions(selectedClient?.id).length})
              </Typography>
              {getClientTransactions(selectedClient?.id).length > 0 ? (
                <List>
                  {getClientTransactions(selectedClient?.id).map((transaction, index) => (
                    <React.Fragment key={transaction.id}>
                      <ListItem 
                        sx={{ 
                          background: 'rgba(255, 255, 255, 0.1)',
                          borderRadius: 2,
                          mb: 1,
                          border: '1px solid rgba(255, 255, 255, 0.2)'
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar sx={{ 
                            background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                            color: 'white'
                          }}>
                            <PaymentIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Typography sx={{ color: 'white', fontWeight: 600 }}>
                              {transaction.description}
                            </Typography>
                          }
                          secondary={
                            <Box>
                              <Typography variant="caption" display="block" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                {formatDate(transaction.date)}
                              </Typography>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#4ade80' }}>
                                {formatCurrency(transaction.montant)}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                      {index < getClientTransactions(selectedClient?.id).length - 1 && 
                        <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.2)', my: 1 }} />
                      }
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Card sx={{ p: 3, textAlign: 'center', background: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)', borderRadius: 2 }}>
                  <PaymentIcon sx={{ fontSize: 48, color: 'rgba(255, 255, 255, 0.5)', mb: 2 }} />
                  <Typography variant="h6" sx={{ color: 'white', mb: 1 }}>Aucune transaction</Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Ce client n'a pas encore effectué de transaction.
                  </Typography>
                </Card>
              )}
            </Box>
          )}

          {/* Tab Panel 3: Statistics */}
          {detailsTab === 3 && (
            <Box sx={{ p: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Card sx={{ p: 3, textAlign: 'center', background: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)', borderRadius: 2 }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#60a5fa', mb: 1 }}>
                      {selectedClient?.nombreReservations || 0}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      Réservations Total
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card sx={{ p: 3, textAlign: 'center', background: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)', borderRadius: 2 }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#4ade80', mb: 1 }}>
                      {formatCurrency(selectedClient?.totalDepense || 0)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      Total Dépensé
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card sx={{ p: 3, textAlign: 'center', background: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)', borderRadius: 2 }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#c084fc', mb: 1 }}>
                      {selectedClient?.totalDepense && selectedClient?.nombreReservations ? 
                        formatCurrency(selectedClient.totalDepense / selectedClient.nombreReservations) : 
                        formatCurrency(0)
                      }
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      Panier Moyen
                    </Typography>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button 
            onClick={() => setOpenDetailsDialog(false)} 
            variant="outlined"
            sx={{ 
              borderRadius: 2,
              borderColor: 'rgba(255, 255, 255, 0.2)',
              color: 'rgba(255, 255, 255, 0.7)',
              '&:hover': {
                borderColor: 'rgba(255, 255, 255, 0.4)',
                color: 'white',
                backgroundColor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            Fermer
          </Button>
          <Button 
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => {
              setOpenDetailsDialog(false);
              handleOpenDialog(selectedClient);
            }}
            sx={{ 
              borderRadius: 2,
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              '&:hover': {
                background: 'linear-gradient(135deg, #4f46e5, #7c3aed)'
              }
            }}
          >
            Modifier Client
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({...snackbar, open: false})}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        sx={{ mb: 2, mr: 2 }}
      >
        <Alert 
          onClose={() => setSnackbar({...snackbar, open: false})} 
          severity={snackbar.severity}
          sx={{ 
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            border: '1px solid',
            borderColor: snackbar.severity === 'success' ? 'rgba(16, 185, 129, 0.2)' : 
                       snackbar.severity === 'error' ? 'rgba(239, 68, 68, 0.2)' : 
                       'rgba(245, 158, 11, 0.2)'
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Clients; 